export const CURRENCIES = ["usd", "kes", "ngn", "ghs", "zar", "xof"];

export const CURRENCY_META = {
  usd: { code: "usd", label: "USD · Card", note: "Pay by card (Visa, Mastercard)." },
  kes: { code: "kes", label: "KES · M-Pesa", note: "Pay with M-Pesa, card, or bank transfer via Paystack." },
  ngn: { code: "ngn", label: "NGN · Bank/Card", note: "Pay by card, bank transfer, or USSD via Paystack." },
  ghs: { code: "ghs", label: "GHS · MoMo", note: "Pay with mobile money or card via Paystack." },
  zar: { code: "zar", label: "ZAR · Card", note: "Pay by card or bank transfer via Paystack." },
  xof: { code: "xof", label: "XOF · MoMo", note: "Pay with mobile money or card in CFA francs via Paystack." },
};

const SYMBOL = {
  usd: "$", kes: "KES\u00a0", ngn: "\u20a6", ghs: "GH\u20b5", zar: "R", xof: "CFA\u00a0",
};

const TZ_MAP = {
  "Africa/Lagos": "ngn",
  "Africa/Accra": "ghs",
  "Africa/Nairobi": "kes",
  "Africa/Johannesburg": "zar",
  "Africa/Abidjan": "xof",
  "Africa/Dakar": "xof",
  "Africa/Bamako": "xof",
  "Africa/Ouagadougou": "xof",
  "Africa/Lome": "xof",
  "Africa/Porto-Novo": "xof",
  "Africa/Niamey": "xof",
};

const LOCALE_MAP = {
  NG: "ngn", GH: "ghs", KE: "kes", ZA: "zar",
  CI: "xof", SN: "xof", ML: "xof", BF: "xof", TG: "xof", BJ: "xof", NE: "xof", GW: "xof",
};

export function geoCurrency() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    if (TZ_MAP[tz]) return TZ_MAP[tz];
    const region = (navigator.language || "").split("-")[1];
    if (region && LOCALE_MAP[region.toUpperCase()]) return LOCALE_MAP[region.toUpperCase()];
  } catch (e) { /* ignore */ }
  return null;
}

export function detectCurrency() {
  try {
    const saved = localStorage.getItem("fl_currency");
    if (CURRENCIES.includes(saved)) return saved;
  } catch (e) { /* ignore */ }
  return geoCurrency() || "usd";
}

export function setCurrency(c) {
  try { localStorage.setItem("fl_currency", c); } catch (e) { /* ignore */ }
}

export const MARKETS = [
  { code: "us", label: "United States", currency: "usd" },
  { code: "ke", label: "Kenya", currency: "kes" },
  { code: "ng", label: "Nigeria", currency: "ngn" },
  { code: "gh", label: "Ghana", currency: "ghs" },
  { code: "za", label: "South Africa", currency: "zar" },
  { code: "wa", label: "West Africa — CFA Franc markets", currency: "xof",
    sub: "Côte d'Ivoire, Senegal, Benin, Burkina Faso, Mali, Niger, Togo & Guinea-Bissau" },
  { code: "ug", label: "Uganda", currency: "usd", sub: "Billed in USD" },
  { code: "tz", label: "Tanzania", currency: "usd", sub: "Billed in USD" },
  { code: "rw", label: "Rwanda", currency: "usd", sub: "Billed in USD" },
  { code: "eg", label: "Egypt", currency: "usd", sub: "Billed in USD" },
  { code: "zm", label: "Zambia", currency: "usd", sub: "Billed in USD" },
  { code: "intl", label: "Other International", currency: "usd" },
];

export function marketByCode(code) {
  return MARKETS.find((m) => m.code === code) || MARKETS[0];
}

export function currencyForMarket(code) {
  return marketByCode(code).currency;
}

export function detectMarket() {
  try {
    const saved = localStorage.getItem("fl_market");
    if (saved && MARKETS.some((m) => m.code === saved)) return saved;
  } catch (e) { /* ignore */ }
  const cur = detectCurrency();
  const found = MARKETS.find((m) => m.currency === cur);
  return found ? found.code : "us";
}

export function setMarket(code) {
  const m = marketByCode(code);
  try { localStorage.setItem("fl_market", m.code); } catch (e) { /* ignore */ }
  setCurrency(m.currency);
}

export function detectedMarketHint() {
  try {
    if (localStorage.getItem("fl_market")) return null;      // user already has a market
    if (localStorage.getItem("fl_market_hint")) return null; // hint already shown/dismissed
  } catch (e) { /* ignore */ }
  const cur = geoCurrency();
  if (!cur || cur === "usd") return null;                    // only hint for detected local markets
  return MARKETS.find((m) => m.currency === cur) || null;
}

export function dismissMarketHint() {
  try { localStorage.setItem("fl_market_hint", "1"); } catch (e) { /* ignore */ }
}

export function formatAmount(currency, value) {
  const s = SYMBOL[currency] || "";
  const n = Number(value || 0);
  const num = currency === "usd" ? n : n.toLocaleString();
  return `${s}${num}`;
}

export function launchActive(pricing, currency) {
  const l = pricing?.tiers?.full?.launch?.[currency];
  if (!l || !l.active) return false;
  if (l.ends && new Date(l.ends) < new Date()) return false;
  return true;
}

export function focusPrice(pricing, currency) {
  const v = pricing?.tiers?.focus?.prices?.[currency];
  return v == null ? null : v;
}

export function fullLaunchPrice(pricing, currency) {
  const v = pricing?.tiers?.full?.launch_prices?.[currency];
  return v == null ? null : v;
}

export function fullRegularPrice(pricing, currency) {
  const v = pricing?.tiers?.full?.regular_prices?.[currency];
  return v == null ? null : v;
}

export function fullEffectivePrice(pricing, currency) {
  return launchActive(pricing, currency) ? fullLaunchPrice(pricing, currency) : fullRegularPrice(pricing, currency);
}

export function priceValue(pricing, tier, currency) {
  return tier === "focus" ? focusPrice(pricing, currency) : fullEffectivePrice(pricing, currency);
}

export function formatPrice(pricing, tier, currency) {
  const v = priceValue(pricing, tier, currency);
  if (v == null) return "";
  return formatAmount(currency, v);
}
